/**
 * MapView.tsx — MapLibre GL map initialiser + MapContext provider (RESEARCH Pattern 7)
 *
 * - Initialises a MapLibre Map centred on New Jersey (MAP-04 default view).
 * - Provides the map instance to children via MapContext.Provider after 'load' fires
 *   (Pitfall 5: children must not call addSource/addLayer before the map is ready).
 * - Calls onBboxChange with the current viewport [west, south, east, north] on load
 *   and on every 'moveend' event, enabling TractLayer to refresh the choropleth.
 * - Renders children as React content overlaying the WebGL canvas; the outer div is
 *   position:relative so overlay components can use position:absolute.
 */
import { useEffect, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
// T-460: our theming overrides for MapLibre's chrome (popup, close button, zoom controls).
// Imported AFTER maplibre-gl.css so these rules win the cascade.
import './mapChrome.css'
import { MapContext } from './MapContext'
import { Spinner } from '../ui/spinner'
import { track, round2 } from '../../lib/analytics'
import { tierForZoom } from '../../lib/mapTiers'
import { currentResolvedTheme } from '../../lib/theme'

// T-126: idle debounce for the map_viewport_changed event. moveend fires many times
// during a single pan/zoom gesture; we wait for 1.5 s of stillness and emit ONE event
// per settled viewport — never a raw move/zoom frame.
const VIEWPORT_IDLE_MS = 1500

// T-092 A1: national default view. The opening camera is the continental-US centroid at a
// national zoom — NOT a hardcoded state — so an operator anywhere lands on the whole map,
// not New Jersey. When loaded coverage is known (initialBounds), the map fits that extent
// on load instead (see below); this CONUS view is the fallback when coverage is empty.
//
// T-308: it is also the fallback when the coverage extent is not a usable camera. Coverage
// now includes Hawaii, and the union bbox of the mainland + HI centres on open Pacific — so
// fitting it made every CONUS user zoom back to the mainland. The caller (MapShell, via
// lib/coverageCamera.ts) decides that and passes null here; reaching HI is then one
// deliberate zoom/pan rather than a cost every user pays on open. The rule is deliberately
// keyed on non-contiguity, not on Hawaii: AK's Aleutians cross the antimeridian, so its
// union extent is ~[-179,+179] and fitting it would frame the planet.
const CONUS_CENTER: [number, number] = [-98.6, 39.8]
const CONUS_ZOOM = 4

// CARTO basemaps — clean, professional, no API key required. Positron (light) has a drop-in
// dark counterpart, Dark Matter, with the same schema — so the theme swap is a URL choice.
// T-156: the basemap is chosen by the resolved theme at map-init (low-effort per BEAD-0063).
// T-160 (BEAD-0068): the basemap now also swaps LIVE on a mid-session theme toggle — see the
// MutationObserver + styleEpoch machinery below.
const BASE_STYLE_LIGHT = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json'
const BASE_STYLE_DARK = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json'

/** The CARTO basemap URL for a resolved theme. */
function basemapStyleFor(resolved: 'light' | 'dark'): string {
  return resolved === 'dark' ? BASE_STYLE_DARK : BASE_STYLE_LIGHT
}

// T-160: the theme actually in effect is whatever `.dark` the chrome shows on <html> — the
// single source of truth the ThemeToggle and the system-pref listener both write to (lib/
// theme.ts applyResolvedTheme). Reading the DOM class (not the stored choice) means the map
// follows the exact same signal as the rest of the chrome, including a live OS-pref change.
function resolvedThemeFromDom(): 'light' | 'dark' {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
    ? 'dark'
    : 'light'
}

/** Returns [west, south, east, north] for the current map viewport. */
function getBbox(map: maplibregl.Map): [number, number, number, number] {
  const b = map.getBounds()
  return [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]
}

interface MapViewProps {
  children?: React.ReactNode
  /** Called with the viewport bbox on load and after every pan/zoom ends. */
  onBboxChange?: (bbox: [number, number, number, number]) => void
  /** T-010: called with the map zoom on load and after every pan/zoom ends, so the
   *  parent can pick the active LOD tier (county vs tract) + geometry detail. */
  onZoomChange?: (zoom: number) => void
  /** T-092 A1: opening camera derived from the loaded coverage extent
   *  [west, south, east, north] (from GET /api/coverage via useCoverage). When present, the
   *  map fits these bounds once — whether coverage resolves before or after 'load'. When
   *  null/absent the map stays on the CONUS national default. Never overrides a later user pan.
   *  T-308: null now also means "coverage is non-contiguous, so its union extent is not a
   *  camera" — MapShell decides via lib/coverageCamera.ts. This side of the contract is
   *  unchanged: bounds → fit them, null → national default. */
  initialBounds?: [number, number, number, number] | null
  /** T-237: called with the map CENTER + zoom on load and after every pan/zoom ends, so the
   *  URL-state controller can sync #zoom/lat/lng. Distinct from onBboxChange because the hash
   *  encodes the true center (bbox-midpoint latitude drifts under Web Mercator and would not
   *  round-trip on reload). */
  onViewportChange?: (v: { center: [number, number]; zoom: number }) => void
  /** T-237: deep-link opening camera parsed from the URL hash. When present it seeds the
   *  constructor center/zoom AND suppresses the coverage fit (the shared/reloaded viewport wins
   *  over the coverage default). Absent → the existing CONUS-default / coverage-fit behaviour. */
  initialViewport?: { center: [number, number]; zoom: number } | null
  /** T-237: an imperative Back/Forward restore target. Each distinct `nonce` triggers one
   *  jumpTo(center, zoom) — how MapShell moves the camera to a popped history entry. */
  commandedViewport?: { center: [number, number]; zoom: number; nonce: number } | null
}

export function MapView({
  children,
  onBboxChange,
  onZoomChange,
  initialBounds,
  onViewportChange,
  initialViewport,
  commandedViewport,
}: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null)
  // T-160 (BEAD-0068): incremented after each live basemap swap finishes loading. It keys the
  // MapContext subtree, so every layer child remounts and re-adds its source/layer against the
  // fresh style (setStyle drops all custom layers). See the theme observer in the mount effect.
  const [styleEpoch, setStyleEpoch] = useState(0)
  // Stable refs so the effect closures do not stale-close over an old callback
  const onBboxChangeRef = useRef(onBboxChange)
  onBboxChangeRef.current = onBboxChange
  const onZoomChangeRef = useRef(onZoomChange)
  onZoomChangeRef.current = onZoomChange
  // T-237: latest viewport callback, mirrored so the once-mounted map effect never stale-closes it.
  const onViewportChangeRef = useRef(onViewportChange)
  onViewportChangeRef.current = onViewportChange
  // T-237: read the deep-link camera once — it seeds the constructor and is intentionally NOT a
  // dep of the mount effect (a later hash change must not reconstruct the map).
  const initialViewportRef = useRef(initialViewport)
  // T-092 A1: the latest coverage extent + a one-shot guard. Coverage can resolve before or
  // after the map's 'load'; we fit exactly once and never yank the camera from under a user
  // who has already panned.
  const initialBoundsRef = useRef(initialBounds)
  initialBoundsRef.current = initialBounds
  const didInitialFitRef = useRef(false)

  /** Fit the coverage extent once, if one is available and we haven't already. */
  const fitCoverageOnce = (map: maplibregl.Map) => {
    if (didInitialFitRef.current) return
    const b = initialBoundsRef.current
    if (!b) return
    didInitialFitRef.current = true
    const [west, south, east, north] = b
    // duration:0 → snap, no visible pan from the CONUS default the map constructed with.
    map.fitBounds(
      [
        [west, south],
        [east, north],
      ],
      { padding: 40, duration: 0 },
    )
  }

  // T-126: debounce timer + last-trigger for the map_viewport_changed analytics event.
  const viewportTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const viewportTriggerRef = useRef<'load' | 'gesture'>('load')

  useEffect(() => {
    if (!containerRef.current) return

    // T-237: a deep-linked viewport (from the URL hash) opens the map there and suppresses the
    // coverage fit below; absent, we keep the CONUS national default + coverage-fit behaviour.
    const deepLink = initialViewportRef.current
    if (deepLink) didInitialFitRef.current = true
    const map = new maplibregl.Map({
      container: containerRef.current,
      // T-156: dark basemap when the resolved theme is dark, else light (chosen once at init).
      style: currentResolvedTheme() === 'dark' ? BASE_STYLE_DARK : BASE_STYLE_LIGHT,
      center: deepLink ? deepLink.center : CONUS_CENTER,
      zoom: deepLink ? deepLink.zoom : CONUS_ZOOM,
    })

    // T-126: (re)arm the idle debounce; when the viewport has been still for
    // VIEWPORT_IDLE_MS, emit one map_viewport_changed with coarse (2dp) center.
    const scheduleViewportCapture = (trigger: 'load' | 'gesture') => {
      viewportTriggerRef.current = trigger
      if (viewportTimerRef.current) clearTimeout(viewportTimerRef.current)
      viewportTimerRef.current = setTimeout(() => {
        const center = map.getCenter()
        const zoom = map.getZoom()
        track('map_viewport_changed', {
          zoom: round2(zoom),
          zoom_tier: tierForZoom(zoom),
          center_lat: round2(center.lat),
          center_lng: round2(center.lng),
          trigger: viewportTriggerRef.current,
        })
      }, VIEWPORT_IDLE_MS)
    }

    // T-237: emit the true center + zoom for URL-hash sync (see onViewportChange prop doc).
    const reportViewport = (m: maplibregl.Map) => {
      const c = m.getCenter()
      onViewportChangeRef.current?.({ center: [c.lng, c.lat], zoom: m.getZoom() })
    }
    // Named handlers so the cleanup can detach exactly these references (BEAD-0037).
    const handleLoad = () => {
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
      setMapInstance(map)
      // T-092 A1: if coverage resolved before load, open on that extent; then report the
      // resulting viewport so the parent picks the right LOD tier from the first frame.
      fitCoverageOnce(map)
      onBboxChangeRef.current?.(getBbox(map))
      onZoomChangeRef.current?.(map.getZoom())
      reportViewport(map)
      scheduleViewportCapture('load')
    }
    const handleMoveEnd = () => {
      onBboxChangeRef.current?.(getBbox(map))
      onZoomChangeRef.current?.(map.getZoom())
      reportViewport(map)
      scheduleViewportCapture('gesture')
    }

    map.on('load', handleLoad)
    map.on('moveend', handleMoveEnd)

    // T-160 (BEAD-0068): live basemap swap on a mid-session theme toggle. The chrome re-themes
    // instantly by toggling `.dark` on <html>; mirror that on the MapLibre basemap here.
    // setStyle(diff:false) does a clean teardown/rebuild — dropping our custom tract/POI/road/
    // pin layers along with the basemap — so once the NEW style has loaded we bump styleEpoch,
    // which re-keys the MapContext subtree and remounts every layer child to re-add its
    // source+layer against the fresh style. Each layer's cleanup is getLayer-guarded, so the
    // drop-then-remount can't throw. setStyle preserves the camera, so there's no viewport jump.
    let basemapTheme = resolvedThemeFromDom()
    const swapBasemapIfThemeChanged = () => {
      const next = resolvedThemeFromDom()
      if (next === basemapTheme) return
      basemapTheme = next
      map.setStyle(basemapStyleFor(next), { diff: false })
      map.once('style.load', () => setStyleEpoch((e) => e + 1))
    }
    const themeObserver = new MutationObserver(swapBasemapIfThemeChanged)
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })

    return () => {
      // T-160: stop mirroring theme changes onto a map that's being torn down.
      themeObserver.disconnect()
      // BEAD-0037 sign-out crash: detach our own listeners and stop() in-flight camera/
      // frame work BEFORE remove() nulls the map's style. Otherwise a queued animation or
      // handler could resume against a removed map and throw (blank white page).
      map.off('load', handleLoad)
      map.off('moveend', handleMoveEnd)
      if (viewportTimerRef.current) clearTimeout(viewportTimerRef.current)
      map.stop()
      map.remove()
      setMapInstance(null)
    }
    // intentionally no deps — run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // T-092 A1: coverage that resolves AFTER 'load' — fit it once here. fitBounds emits a
  // 'moveend', so the LOD-tier callbacks re-fire through handleMoveEnd; the didInitialFitRef
  // guard means this never fights a user who has already moved the map.
  useEffect(() => {
    if (mapInstance && initialBounds && !didInitialFitRef.current) {
      fitCoverageOnce(mapInstance)
    }
    // fitCoverageOnce reads refs; only mapInstance/initialBounds gate whether it runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapInstance, initialBounds])

  // T-237: Back/Forward restore. Each new `commandedViewport.nonce` jumps the camera to the popped
  // history entry's viewport. jumpTo (not flyTo) — a history restore should be instant, not a slow
  // pan, and it fires 'moveend' so the LOD tier + URL sync self-correct to the restored position.
  const lastCommandNonceRef = useRef<number | null>(null)
  useEffect(() => {
    if (!mapInstance || !commandedViewport) return
    if (lastCommandNonceRef.current === commandedViewport.nonce) return
    lastCommandNonceRef.current = commandedViewport.nonce
    mapInstance.jumpTo({ center: commandedViewport.center, zoom: commandedViewport.zoom })
  }, [mapInstance, commandedViewport])

  return (
    <div className="relative w-full h-full">
      {/* WebGL canvas target — size via h-full/w-full, NOT absolute inset-0:
          MapLibre injects `.maplibregl-map { position: relative }` onto this node,
          which overrides the `absolute` utility (same specificity, loaded later) and
          collapses `inset-0` to 0 height. Height-based sizing is immune to that. */}
      <div ref={containerRef} className="h-full w-full" />
      {/* T-339: first-load overlay. The MapContext children (incl. TileLoadingIndicator) mount
          only AFTER the map's 'load' fires; until then the canvas is blank, which reads as
          "broken" rather than "still loading". This covers that initial-init window and clears
          the instant the map is ready (mapInstance set). pointer-events-none so it never blocks
          interaction; bg-card/text-foreground are theme tokens (legible light + dark). */}
      {!mapInstance && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <div className="flex items-center gap-2 rounded-full bg-card/95 px-3 py-1.5 shadow-md text-xs font-medium text-foreground">
            <Spinner size={14} label="Loading map" />
            Loading map…
          </div>
        </div>
      )}
      {/* Layer + overlay children rendered after map loads */}
      {mapInstance && (
        // T-160: `key={styleEpoch}` remounts the whole layer subtree after a live basemap swap
        // so each layer child re-adds its source/layer against the fresh style. Durable UI state
        // (selection, filters, pin, viewport) lives in MapShell above this, so it survives the
        // remount; only ephemeral child-local state (e.g. an in-progress search box) resets.
        <MapContext.Provider value={mapInstance} key={styleEpoch}>
          {children}
        </MapContext.Provider>
      )}
    </div>
  )
}
